import { useState } from 'react';

import { Button } from '~/components/ui/Button';
import { usePushNotifications } from '~/hooks/usePushNotifications';

interface PushNotificationSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PushNotificationSettingsModal({
  isOpen,
  onClose,
}: PushNotificationSettingsModalProps) {
  const {
    isSupported,
    permission,
    subscription,
    isLoading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
  } = usePushNotifications();

  const [isEnabling, setIsEnabling] = useState(false);

  const handleEnable = async () => {
    setIsEnabling(true);
    try {
      const permissionGranted = permission === 'granted' || (await requestPermission());
      if (permissionGranted) {
        await subscribe();
      }
    } finally {
      setIsEnabling(false);
    }
  };

  const handleDisable = async () => {
    setIsEnabling(true);
    try {
      await unsubscribe();
    } finally {
      setIsEnabling(false);
    }
  };

  const handleTestNotification = async () => {
    if (!subscription) return;

    try {
      const response = await fetch('/api/push-notify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: 'Test Notification',
          body: 'This is a test push notification to verify your setup is working!',
          url: '/dashboard',
        }),
      });

      if (response.ok) {
        alert('Test notification sent successfully! Check your device for the notification.');
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send notification');
      }
    } catch (error) {
      console.error('Test notification error:', error);
      alert('Failed to send test notification. Please try again.');
    }
  };

  if (!isOpen) return null;

  const isSubscribed = !!subscription;
  const canEnable = permission === 'granted' || permission === 'default';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Push Notifications</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-6">
          {!isSupported ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 text-sm">
                Push notifications are not supported in this browser. Please use a modern browser
                like Chrome, Firefox, Edge, or Safari.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-md font-medium">Browser Notifications</h3>
                    <p className="text-sm text-gray-600">
                      Get notified about new referrals and important updates even when the app is
                      closed.
                    </p>
                  </div>

                  <div className="flex items-center space-x-3">
                    {isSubscribed && (
                      <Button
                        onClick={handleDisable}
                        disabled={isLoading || isEnabling}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        {isEnabling ? 'Disabling...' : 'Disable'}
                      </Button>
                    )}

                    {permission === 'denied' && (
                      <p className="text-sm text-red-600">
                        Blocked by browser. Enable in settings.
                      </p>
                    )}
                  </div>
                </div>

                {isSubscribed && (
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div>
                      <h4 className="text-sm font-medium">Test Notification</h4>
                      <p className="text-xs text-gray-500">
                        Send a test notification to verify setup
                      </p>
                    </div>
                    <Button onClick={handleTestNotification} variant="secondary" size="sm">
                      Test
                    </Button>
                  </div>
                )}
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="text-xs text-gray-500 space-y-1">
                <p>
                  <strong>Status:</strong>{' '}
                  {isSubscribed
                    ? 'Enabled'
                    : permission === 'granted'
                      ? 'Ready to enable'
                      : permission === 'denied'
                        ? 'Blocked'
                        : 'Not configured'}
                </p>
                {isSubscribed && (
                  <p>
                    <strong>Endpoint:</strong> {subscription.endpoint.substring(0, 50)}...
                  </p>
                )}
              </div>
            </>
          )}

          <div className="flex justify-between items-center pt-4">
            <Button type="button" onClick={onClose} variant="outline" size="sm">
              Close
            </Button>

            {!isSubscribed && canEnable && (
              <Button onClick={handleEnable} disabled={isLoading || isEnabling} size="sm">
                {isEnabling ? 'Enabling...' : 'Enable'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
